import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { useActiveOutlet, type OutletLite } from '../lib/use-active-outlet.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import {
  PRODUCT_SALES_CONFIG_DOC_ID,
  productSalesConfigRepoFor,
  productsRepoFor,
} from '../lib/repositories/outlet-repos.js';
import { tenantsRepo } from '../lib/repositories/tenants-repo.js';
import { logger } from '../lib/logger.js';
import { PAGE_FRAME_MAX } from '../lib/layout.js';
import { defaultPlanId, defaultPlans } from '@kilat/config-seed';
import {
  excludedOutletIdsFromMap,
  money,
  outletAssignmentMap,
  planAllowsProductSales,
  type Product,
  type TenantProfile,
} from '@kilat/shared';

/**
 * /products — operator CRUD for product cross-sell (Pro).
 *
 * Physical items an operator sells alongside a wash (wax, air-freshener, a
 * drink). Tenant-library docs at `tenants/{tid}/products/{id}`, flat-priced,
 * per-outlet visibility via `excludedOutletIds`. Mirrors `/loyalty/packages`.
 *
 * Two-condition gate, both surfaced here:
 *   (a) plan entitlement — resolved from `profile.planId`. Not Pro → an
 *       "upgrade" state; the list + editor never render.
 *   (b) operator opt-in — the `productSales/config.enabled` toggle. Writable
 *       only once entitled. Off → products stay hidden from customers.
 * The server (bookTicket) re-checks BOTH before pricing a product, so this
 * screen is a convenience gate, never the boundary.
 *
 * No-dead-ends: every state explicit (signed-out / no-tenant / loading /
 * not-entitled / empty / error / populated).
 */
export function ProductsPage() {
  const { user, claims, loading: authLoading } = useAuth();
  const { t } = useI18n();
  const { outlets, loading: outletsLoading } = useActiveOutlet();

  const tenantId =
    typeof claims?.['defaultTenant'] === 'string' ? (claims['defaultTenant'] as string) : null;
  const role =
    tenantId && claims !== null
      ? (((claims as { roles?: Record<string, string> }).roles ?? {})[tenantId] ?? null)
      : null;
  const canEdit = role === 'owner' || role === 'manager';

  // Tenant profile → plan entitlement (one-shot; planId rarely changes).
  const [profile, setProfile] = useState<TenantProfile | null>(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    (async () => {
      try {
        const doc = await tenantsRepo.get(tenantId);
        if (cancelled) return;
        setProfile(doc?.profile ?? null);
        setProfileLoaded(true);
      } catch (e) {
        logger.error('products: tenant load failed', { error: String(e) });
        if (!cancelled) setProfileLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const entitled = useMemo(() => {
    if (!profile) return false;
    const plan =
      defaultPlans.find((p) => p.id === profile.planId) ??
      defaultPlans.find((p) => p.id === defaultPlanId);
    return planAllowsProductSales(plan);
  }, [profile]);

  // Opt-in toggle (one-shot read + optimistic local state).
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [togglePending, setTogglePending] = useState(false);
  useEffect(() => {
    if (!tenantId || !entitled) return;
    let cancelled = false;
    (async () => {
      try {
        const cfg = await productSalesConfigRepoFor(tenantId).get(PRODUCT_SALES_CONFIG_DOC_ID);
        if (!cancelled) setEnabled(cfg?.enabled ?? false);
      } catch (e) {
        logger.error('products: config load failed', { error: String(e) });
        if (!cancelled) setEnabled(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId, entitled]);

  const sub = useMemo(() => {
    if (!tenantId || !entitled) return null;
    const repo = productsRepoFor(tenantId);
    return (cb: (vs: Product[]) => void) => repo.listSnapshot(cb);
  }, [tenantId, entitled]);
  const {
    data: products,
    loading,
    error,
  } = useTrackedSnapshot<Product[]>(['products', tenantId], sub);

  const [editing, setEditing] = useState<Product | 'new' | null>(null);

  async function toggleEnabled(next: boolean) {
    if (!tenantId || !canEdit) return;
    setTogglePending(true);
    try {
      await productSalesConfigRepoFor(tenantId).set(PRODUCT_SALES_CONFIG_DOC_ID, { enabled: next });
      setEnabled(next);
    } catch (e) {
      logger.error('products: toggle save failed', { error: String(e) });
    } finally {
      setTogglePending(false);
    }
  }

  if (authLoading)
    return (
      <Shell>
        <Skeleton />
      </Shell>
    );
  if (!user)
    return (
      <Shell>
        <EmptyState
          title={t('dashboard.products.signedOutTitle')}
          body={t('dashboard.products.signedOutBody')}
          ctaLabel={t('dashboard.products.signIn')}
          ctaTo="/"
        />
      </Shell>
    );
  if (!tenantId)
    return (
      <Shell>
        <EmptyState
          title={t('dashboard.products.noTenantTitle')}
          body={t('dashboard.products.noTenantBody')}
          ctaLabel={t('dashboard.products.welcomeCta')}
          ctaTo="/welcome"
        />
      </Shell>
    );
  if (!profileLoaded || outletsLoading)
    return (
      <Shell>
        <Skeleton />
      </Shell>
    );

  // Plan entitlement gate — not Pro → an upgrade next-action, never the list.
  if (!entitled)
    return (
      <Shell>
        <Header />
        <EmptyState
          title={t('dashboard.products.upgradeTitle')}
          body={t('dashboard.products.upgradeBody')}
          ctaLabel={t('dashboard.products.upgradeCta')}
          ctaTo="/billing"
        />
      </Shell>
    );

  if (error)
    return (
      <Shell>
        <Header />
        <EmptyState
          title={t('dashboard.products.loadErrorTitle')}
          body={String(error.message ?? error)}
          ctaLabel={t('dashboard.states.reload')}
          ctaTo="/products"
        />
      </Shell>
    );
  if (loading || products === null)
    return (
      <Shell>
        <Header />
        <Skeleton />
      </Shell>
    );

  const sorted = products.slice().sort((a, b) => a.priceSen - b.priceSen);
  const outletCount = outlets.length;

  return (
    <Shell>
      <Header
        action={
          canEdit ? (
            <button type="button" onClick={() => setEditing('new')} style={primaryButton}>
              {t('dashboard.products.newProduct')}
            </button>
          ) : null
        }
      />

      <OptInCard
        enabled={enabled}
        canEdit={canEdit}
        pending={togglePending}
        onToggle={toggleEnabled}
        hasProducts={sorted.length > 0}
      />

      {sorted.length === 0 ? (
        <EmptyState
          title={t('dashboard.products.emptyTitle')}
          body={
            canEdit
              ? t('dashboard.products.emptyBodyCanEdit')
              : t('dashboard.products.emptyBodyReadOnly')
          }
        />
      ) : (
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'grid',
            gap: 'var(--space-3)',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))',
          }}
        >
          {sorted.map((p) => (
            <li key={p.id}>
              <ProductCard
                product={p}
                outlets={outlets}
                canEdit={canEdit}
                onEdit={() => setEditing(p)}
              />
            </li>
          ))}
        </ul>
      )}

      {editing ? (
        <ProductEditor
          tenantId={tenantId}
          outlets={outlets}
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}

      {outletCount === 0 && canEdit ? (
        <p style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
          {t('dashboard.products.noOutletsPre')}{' '}
          <Link to="/outlets" style={{ color: 'var(--color-primary)' }}>
            {t('dashboard.products.addOutletLink')}
          </Link>
          .
        </p>
      ) : null}
    </Shell>
  );
}

// ── components ─────────────────────────────────────────────────────────

function Header({ action }: { readonly action?: React.ReactNode }) {
  const { t } = useI18n();
  return (
    <header
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        gap: 'var(--space-3)',
        flexWrap: 'wrap',
      }}
    >
      <div>
        <p style={metaEyebrow}>{t('dashboard.products.eyebrow')}</p>
        <h1 style={{ margin: 'var(--space-1) 0 0 0', fontSize: 'var(--text-h1)', fontWeight: 600 }}>
          {t('dashboard.products.title')}
        </h1>
        <p
          style={{
            margin: 'var(--space-2) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            maxWidth: 640,
            lineHeight: 1.5,
          }}
        >
          {t('dashboard.products.intro')}
        </p>
      </div>
      {action}
    </header>
  );
}

function OptInCard({
  enabled,
  canEdit,
  pending,
  onToggle,
  hasProducts,
}: {
  readonly enabled: boolean | null;
  readonly canEdit: boolean;
  readonly pending: boolean;
  readonly onToggle: (next: boolean) => void;
  readonly hasProducts: boolean;
}) {
  const { t } = useI18n();
  const on = enabled === true;
  return (
    <section
      style={{
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-4) var(--space-5)',
        boxShadow: 'var(--shadow-1)',
        display: 'grid',
        gap: 'var(--space-2)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 'var(--space-3)',
          alignItems: 'center',
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
            {t('dashboard.products.toggleTitle')}
          </h2>
          <p
            style={{
              margin: 'var(--space-1) 0 0 0',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              lineHeight: 1.5,
            }}
          >
            {t('dashboard.products.toggleBody')}
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={on}
          aria-label={t('dashboard.products.toggleAria')}
          disabled={!canEdit || pending || enabled === null}
          onClick={() => onToggle(!on)}
          style={{
            flexShrink: 0,
            width: 52,
            height: 30,
            borderRadius: 'var(--radius-pill)',
            border: '1px solid rgba(15,23,42,0.12)',
            background: on ? 'var(--color-primary)' : 'rgba(15,23,42,0.10)',
            position: 'relative',
            cursor: canEdit && !pending ? 'pointer' : 'not-allowed',
            opacity: canEdit ? 1 : 0.6,
            transition: 'background 160ms ease',
          }}
        >
          <span
            aria-hidden
            style={{
              position: 'absolute',
              top: 3,
              left: on ? 25 : 3,
              width: 22,
              height: 22,
              borderRadius: '50%',
              background: 'var(--color-primary-fg, #fff)',
              boxShadow: 'var(--shadow-1)',
              transition: 'left 160ms ease',
            }}
          />
        </button>
      </div>
      {enabled === false && hasProducts ? (
        <p role="status" style={{ margin: 0, fontSize: 'var(--text-meta)', color: '#b45309' }}>
          {t('dashboard.products.disabledHint')}
        </p>
      ) : null}
    </section>
  );
}

function ProductCard({
  product,
  outlets,
  canEdit,
  onEdit,
}: {
  readonly product: Product;
  readonly outlets: readonly OutletLite[];
  readonly canEdit: boolean;
  readonly onEdit: () => void;
}) {
  const { t } = useI18n();
  const headline =
    product.name['en'] ?? Object.values(product.name)[0] ?? t('dashboard.products.productFallback');
  const excludedHere = product.excludedOutletIds.filter((id) => outlets.some((o) => o.id === id));
  const visibleCount = Math.max(0, outlets.length - excludedHere.length);
  const assignmentLabel =
    outlets.length === 0
      ? t('dashboard.products.assignNoOutlets')
      : excludedHere.length === 0
        ? outlets.length === 1
          ? t('dashboard.products.assignAllOne')
          : t('dashboard.products.assignAllOther', { count: outlets.length })
        : t('dashboard.products.assignSome', { visible: visibleCount, total: outlets.length });
  return (
    <article
      style={{
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-5)',
        boxShadow: 'var(--shadow-1)',
        display: 'grid',
        gap: 'var(--space-3)',
        opacity: product.active ? 1 : 0.7,
      }}
    >
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 'var(--space-2)',
        }}
      >
        <div>
          <h3 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>{headline}</h3>
          {product.maxQtyPerOrder && product.maxQtyPerOrder > 1 ? (
            <p
              style={{
                margin: 'var(--space-1) 0 0 0',
                fontSize: 'var(--text-meta)',
                color: 'var(--color-on-surface-muted)',
              }}
            >
              {t('dashboard.products.maxQtyCard', { count: product.maxQtyPerOrder })}
            </p>
          ) : null}
        </div>
        {!product.active ? <Chip label={t('dashboard.products.inactive')} /> : null}
      </header>
      <span
        style={{ fontSize: 'var(--text-h2)', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
      >
        {money.format(product.priceSen)}
      </span>
      {product.description ? (
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            lineHeight: 1.5,
          }}
        >
          {product.description['en'] ?? Object.values(product.description)[0]}
        </p>
      ) : null}
      <p
        style={{
          margin: 0,
          fontSize: 'var(--text-meta)',
          color: excludedHere.length === 0 ? 'var(--color-on-surface-muted)' : '#b45309',
        }}
      >
        {assignmentLabel}
      </p>
      {canEdit ? (
        <div style={{ marginTop: 'var(--space-2)', display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onEdit} style={ghostButton}>
            {t('dashboard.products.edit')}
          </button>
        </div>
      ) : null}
    </article>
  );
}

function ProductEditor({
  tenantId,
  outlets,
  initial,
  onClose,
}: {
  readonly tenantId: string;
  readonly outlets: readonly OutletLite[];
  readonly initial: Product | null;
  readonly onClose: () => void;
}) {
  const { t } = useI18n();
  const isNew = !initial;
  const [name, setName] = useState(initial?.name['en'] ?? '');
  const [description, setDescription] = useState(initial?.description?.['en'] ?? '');
  const [priceRm, setPriceRm] = useState(String((initial?.priceSen ?? 1000) / 100));
  const [maxQty, setMaxQty] = useState(String(initial?.maxQtyPerOrder ?? 1));
  const [active, setActive] = useState(initial?.active ?? true);

  const outletIds = useMemo(() => outlets.map((o) => o.id), [outlets]);
  const [outletAssignment, setOutletAssignment] = useState<Record<string, boolean>>(() =>
    outletAssignmentMap(initial?.excludedOutletIds, outletIds),
  );

  const [stage, setStage] = useState<
    { kind: 'idle' } | { kind: 'saving' } | { kind: 'error'; message: string }
  >({ kind: 'idle' });

  function toggleOutletAssignment(outletId: string) {
    setOutletAssignment((prev) => ({ ...prev, [outletId]: !(prev[outletId] ?? true) }));
  }

  const visibleAtCount = Object.values(outletAssignment).filter((v) => v).length;
  const allUnchecked = outlets.length > 0 && visibleAtCount === 0;

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setStage({ kind: 'saving' });
    try {
      const priceSen = Math.max(0, Math.round(Number(priceRm) * 100));
      if (!(priceSen > 0)) {
        setStage({ kind: 'error', message: t('dashboard.products.errPrice') });
        return;
      }
      if (allUnchecked) {
        setStage({ kind: 'error', message: t('dashboard.products.errPickOutlet') });
        return;
      }
      const maxQtyInt = Math.max(1, Math.min(99, Math.floor(Number(maxQty) || 1)));
      const excludedOutletIds = excludedOutletIdsFromMap(outletAssignment, outletIds);
      const id = initial?.id ?? `prod-${crypto.randomUUID().slice(0, 12)}`;
      await productsRepoFor(tenantId).set(id, {
        id,
        name: { en: name.trim(), ms: name.trim(), zh: name.trim(), ta: name.trim() },
        ...(description.trim()
          ? {
              description: {
                en: description.trim(),
                ms: description.trim(),
                zh: description.trim(),
                ta: description.trim(),
              },
            }
          : {}),
        priceSen,
        active,
        excludedOutletIds,
        ...(maxQtyInt > 1 ? { maxQtyPerOrder: maxQtyInt } : {}),
      });
      onClose();
    } catch (e) {
      logger.error('product save failed', { error: String(e) });
      setStage({
        kind: 'error',
        message: String((e as Error).message ?? t('dashboard.products.errCouldNotSave')),
      });
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 50,
        padding: 'var(--space-4)',
      }}
      onClick={onClose}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSave}
        style={{
          background: 'var(--color-surface)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-6)',
          maxWidth: 520,
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: 'var(--shadow-3)',
          display: 'grid',
          gap: 'var(--space-4)',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 'var(--text-h2)', fontWeight: 600 }}>
          {isNew ? t('dashboard.products.editorNewTitle') : t('dashboard.products.editorEditTitle')}
        </h2>
        <label style={fieldStack}>
          <span style={fieldLabel}>{t('dashboard.products.nameLabel')}</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('dashboard.products.namePlaceholder')}
            required
            style={inputStyle}
          />
        </label>
        <label style={fieldStack}>
          <span style={fieldLabel}>{t('dashboard.products.descLabel')}</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('dashboard.products.descPlaceholder')}
            rows={2}
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </label>
        <div
          style={{
            display: 'grid',
            gap: 'var(--space-3)',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          }}
        >
          <label style={fieldStack}>
            <span style={fieldLabel}>{t('dashboard.products.priceLabel')}</span>
            <input
              value={priceRm}
              onChange={(e) => setPriceRm(e.target.value)}
              inputMode="decimal"
              style={inputStyle}
            />
          </label>
          <label style={fieldStack}>
            <span style={fieldLabel}>{t('dashboard.products.maxQtyLabel')}</span>
            <input
              value={maxQty}
              onChange={(e) => setMaxQty(e.target.value)}
              inputMode="numeric"
              style={inputStyle}
            />
          </label>
        </div>
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            lineHeight: 1.5,
          }}
        >
          {t('dashboard.products.maxQtyHint')}
        </p>

        {outlets.length > 1 ? (
          <fieldset
            style={{
              border: '1px solid rgba(15,23,42,0.10)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--space-3) var(--space-4)',
              display: 'grid',
              gap: 'var(--space-2)',
            }}
          >
            <legend style={{ ...fieldLabel, padding: '0 var(--space-2)' }}>
              {t('dashboard.products.availableAtLegend')}
            </legend>
            <p
              style={{
                margin: 0,
                fontSize: 'var(--text-meta)',
                color: 'var(--color-on-surface-muted)',
                lineHeight: 1.5,
              }}
            >
              {t('dashboard.products.availableAtHint')}
            </p>
            {outlets.map((o) => {
              const checked = outletAssignment[o.id] ?? true;
              return (
                <label
                  key={o.id}
                  style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleOutletAssignment(o.id)}
                  />
                  <span style={{ fontSize: 'var(--text-meta)' }}>{o.name}</span>
                </label>
              );
            })}
            {allUnchecked ? (
              <p role="alert" style={{ margin: 0, fontSize: 'var(--text-meta)', color: '#b45309' }}>
                {t('dashboard.products.errPickOutlet')}
              </p>
            ) : null}
          </fieldset>
        ) : outlets.length === 1 && outlets[0] ? (
          <p
            style={{
              margin: 0,
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            {t('dashboard.products.singleOutletNote', { name: outlets[0].name })}
          </p>
        ) : null}

        <label style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          <span style={{ fontSize: 'var(--text-meta)' }}>
            {t('dashboard.products.activeCheckbox')}
          </span>
        </label>

        {stage.kind === 'error' ? (
          <p role="alert" style={errorStyle}>
            {stage.message}
          </p>
        ) : null}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
          <button type="button" onClick={onClose} style={ghostButton}>
            {t('dashboard.products.cancel')}
          </button>
          <button
            type="submit"
            disabled={stage.kind === 'saving' || !name.trim()}
            style={{
              ...primaryButton,
              opacity: stage.kind === 'saving' || !name.trim() ? 0.55 : 1,
              cursor: stage.kind === 'saving' ? 'wait' : 'pointer',
            }}
          >
            {stage.kind === 'saving'
              ? t('dashboard.products.saving')
              : isNew
                ? t('dashboard.products.createProduct')
                : t('dashboard.products.saveChanges')}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── shell + helpers ────────────────────────────────────────────────────

function Shell({ children }: { readonly children: React.ReactNode }) {
  return (
    <main
      style={{
        minHeight: '100dvh',
        background: 'rgba(15,23,42,0.02)',
        padding: 'var(--space-6) var(--space-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-5)',
        maxWidth: PAGE_FRAME_MAX,
        margin: '0 auto',
      }}
    >
      {children}
    </main>
  );
}

function Skeleton() {
  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)' }} aria-busy="true">
      <div
        style={{ height: 60, borderRadius: 'var(--radius-md)', background: 'rgba(15,23,42,0.06)' }}
      />
      <div
        style={{
          display: 'grid',
          gap: 'var(--space-3)',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))',
        }}
      >
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              height: 160,
              borderRadius: 'var(--radius-lg)',
              background: 'rgba(15,23,42,0.06)',
            }}
          />
        ))}
      </div>
    </div>
  );
}

function EmptyState({
  title,
  body,
  ctaLabel,
  ctaTo,
}: {
  readonly title: string;
  readonly body: string;
  readonly ctaLabel?: string;
  readonly ctaTo?: string;
}) {
  return (
    <section
      style={{
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-8) var(--space-6)',
        textAlign: 'center',
        boxShadow: 'var(--shadow-1)',
      }}
    >
      <h2 style={{ margin: 0, fontSize: 'var(--text-h2)', fontWeight: 600 }}>{title}</h2>
      <p
        style={{
          margin: 'var(--space-3) auto 0 auto',
          maxWidth: 460,
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          lineHeight: 1.5,
        }}
      >
        {body}
      </p>
      {ctaLabel && ctaTo ? (
        <Link
          to={ctaTo}
          style={{ ...primaryButton, marginTop: 'var(--space-4)', display: 'inline-flex' }}
        >
          {ctaLabel}
        </Link>
      ) : null}
    </section>
  );
}

function Chip({ label }: { readonly label: string }) {
  return (
    <span
      style={{
        padding: '4px 10px',
        borderRadius: 'var(--radius-pill)',
        background: 'rgba(15,23,42,0.06)',
        color: 'var(--color-on-surface-muted)',
        fontSize: 'var(--text-meta)',
        fontWeight: 500,
      }}
    >
      {label}
    </span>
  );
}

const metaEyebrow: React.CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-meta)',
  color: 'var(--color-primary)',
  letterSpacing: 4,
  textTransform: 'uppercase',
  fontWeight: 600,
};

const fieldStack: React.CSSProperties = { display: 'grid', gap: 'var(--space-2)' };
const fieldLabel: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  fontWeight: 500,
};
const inputStyle: React.CSSProperties = {
  padding: '0.625em 0.875em',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid rgba(15,23,42,0.12)',
  fontSize: 'var(--text-body)',
  fontFamily: 'inherit',
  background: 'var(--color-surface)',
  color: 'var(--color-on-surface)',
};
const primaryButton: React.CSSProperties = {
  background: 'var(--color-primary)',
  color: 'var(--color-primary-fg)',
  padding: '0.5em 1em',
  borderRadius: 'var(--radius-md)',
  border: 'none',
  fontSize: 'var(--text-meta)',
  fontWeight: 500,
  cursor: 'pointer',
  boxShadow: 'var(--shadow-1)',
  textDecoration: 'none',
};
const ghostButton: React.CSSProperties = {
  background: 'transparent',
  color: 'var(--color-on-surface-muted)',
  padding: '0.5em 1em',
  border: '1px solid rgba(15,23,42,0.12)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--text-meta)',
  fontWeight: 500,
  cursor: 'pointer',
};
const errorStyle: React.CSSProperties = {
  margin: 0,
  padding: '0.5em 0.75em',
  background: 'rgba(239,68,68,0.06)',
  border: '1px solid rgba(239,68,68,0.22)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--text-meta)',
  color: '#b91c1c',
};
